import {renderRoutes} from '@computerrock/formation-router';
import React, {lazy, Suspense} from 'react';
import config from './config';
import routePaths from './routePaths';
import keycloakAuth from './keycloakAuth';
import arcGISAuth from './arcGISAuth';
import ecsAccessControl from './ecsAccessControl';
import * as applicationLoaderSagas from './application/applicationLoaderSagas';
import * as userProfileLoaderSagas from './user-profiles/userProfileLoaderSagas';
import * as memberLoaderSagas from './members/memberLoaderSagas';
import * as serviceCaseLoaderSagas from './service-cases/serviceCaseLoaderSagas';
import * as serviceAssignmentLoaderSagas from './service-assignments/serviceAssignmentLoaderSagas';
import * as taskLoaderSagas from './tasks/taskLoaderSagas';
import * as dashboardLoaderSagas from './dashboard/dashboardLoaderSagas';
import * as caseLogLoaderSagas from './case-logs/caseLogLoaderSagas';
import * as contractPartnerLoaderSagas from './contract-partners/contractPartnerLoaderSagas';
import ApplicationScreen from './application/ApplicationScreen';
import NotFoundScreen from './application/NotFoundScreen';
import AuthRedirectionScreen from './application/AuthRedirectionScreen';
import ShiftRoleSelectionScreen from './user-profiles/ShiftRoleSelectionScreen';
import DashboardSectionScreen from './dashboard/DashboardSectionScreen';
import DashboardScreen from './dashboard/DashboardScreen';
import MemberScreen from './members/MemberScreen';
import MemberSearchScreen from './members/MemberSearchScreen';
import TaskSearchScreen from './tasks/TaskSearchScreen';
import ServiceCaseSearchScreen from './service-cases/ServiceCaseSearchScreen';
import ServiceCaseScreen from './service-cases/ServiceCaseScreen';
import ContractPartnerSearchScreen from './contract-partners/ContractPartnerSearchScreen';
import ContractPartnerScreen from './contract-partners/ContractPartnerScreen';
import AccessDeniedScreen from './application/AccessDeniedScreen';
import MaintenanceScreen from './application/MaintenanceScreen';

// lazily load ArcGIS-heavy screens to keep them out of main bundle
const ServiceAssignmentSubstituteDriverScreenLazy = lazy(() => import('./service-assignments-substitute-driver/ServiceAssignmentSubstituteDriverScreen'));
const ServiceAssignmentMedicalClearanceScreenLazy = lazy(() => import('./service-assignments-medical-clearance/ServiceAssignmentMedicalClearanceScreen'));
const ServiceAssignmentSickPersonScreenLazy = lazy(() => import('./service-assignments-sick-person/ServiceAssignmentSickPersonScreen'));
const ServiceAssignmentVehicleReturnScreenLazy = lazy(() => import('./service-assignments-vehicle-return/ServiceAssignmentVehicleReturnScreen'));
const ServiceAssignmentVehicleAdditionalScreenLazy = lazy(() => import('./service-assignments-vehicle-additional/ServiceAssignmentVehicleAdditionalScreen'));
const ServiceAssignmentOtherServicesScreenLazy = lazy(() => import('./service-assignments-other-services/ServiceAssignmentOtherServicesScreen'));
const ServiceAssignmentPickupScreenLazy = lazy(() => import('./service-assignments-pickup/ServiceAssignmentPickupScreen'));
const ServiceAssignmentVehicleScreenLazy = lazy(() => import('./service-assignments-vehicle/ServiceAssignmentVehicleScreen'));
const ServiceAssignmentRentalCarScreenLazy = lazy(() => import('./service-assignments-rental-car/ServiceAssignmentRentalCarScreen'));
const ServiceAssignmentAccommodationScreenLazy = lazy(() => import('./service-assignments-accommodation/ServiceAssignmentAccommodationScreen'));
const ServiceAssignmentShortTripScreenLazy = lazy(() => import('./service-assignments-short-trip/ServiceAssignmentShortTripScreen'));
const ServiceAssignmentReturnJourneyScreenLazy = lazy(() => import('./service-assignments-return-journey/ServiceAssignmentReturnJourneyScreen'));
const ServiceAssignmentTrailerReturnScreenLazy = lazy(() => import('./service-assignments-trailer-return/ServiceAssignmentTrailerReturnScreen'));
const ServiceAssignmentDeathEventHelpScreenLazy = lazy(() => import('./service-assignments-death-event-help/ServiceAssignmentDeathEventHelpScreen'));
const ServiceAssignmentMedicineShipmentScreenLazy = lazy(() => import('./service-assignments-medicine-shipment/ServiceAssignmentMedicineShipmentScreen'));
const ServiceAssignmentMedicalAdviceScreenLazy = lazy(() => import('./service-assignments-medical-advice/ServiceAssignmentMedicalAdviceScreen'));
const ServiceAssignmentLegalAdviceScreenLazy = lazy(() => import('./service-assignments-legal-advice/ServiceAssignmentLegalAdviceScreen'));

/**
 * Location object definition (react-router, history)
 *
 * @typedef {Object} Location
 * @property {string} key - key is available for all by HashHistory
 * @property {string} pathname - path part of route
 * @property {string} search - search query part of route
 * @property {string} hash - hash (#) part of route
 * @property {Object} state - user defined state for the route
 */

/**
 * ConnectedRoute object definition (react-router-config, connected-react-router, redux-saga)
 *
 * @typedef {Object} ConnectedRoute
 * @property {string} path - any valid URL path that path-to-regexp understands.
 * @property {?Object} component - React component
 * @property {?function} render - render prop function
 * @property {?Location} location - for matching against different location than one in history
 * @property {?boolean} exact - when true, will only match if the path matches the location.pathname exactly
 * @property {?boolean} string - when true, a path that has a trailing slash will only match a location.pathname
 *                      with a trailing slash
 * @property {?boolean} sensitive - when true, will match if the path is case sensitive.
 * @property {?Array<ConnectedRoute>} routes - sub-routes
 * @property {?Array<Array<[saga, Object]>>} locationChangeSideEffects - Redux sagas (side-effects) to be run
 *                                            with payload object
 */

/**
 * Static route configuration
 *
 * @returns {Array<ConnectedRoute>}
 */
const getRoutes = () => [{
    component: ApplicationScreen,
    locationChangeSideEffects: [],
    routes: [
        window.DYNAMIC_ENV.IS_MAINTENANCE_MODE_ON
        && !config.IS_FALLBACK_SYSTEM // we don't need a maintenance screen on the ECS-FBS
        && window.location.hostname !== config.BACKDOOR_URL
            ? {
                path: '*',
                component: MaintenanceScreen,
            } : {
                path: routePaths.KEYCLOAK_AUTHORIZE,
                exact: true,
                component: AuthRedirectionScreen,
                locationChangeSideEffects: [
                    [keycloakAuth.authorize],
                ],
            },
        {
            path: routePaths.KEYCLOAK_AUTHENTICATE,
            exact: true,
            component: AuthRedirectionScreen,
            locationChangeSideEffects: [
                [keycloakAuth.authenticate],
            ],
        },
        ...(!config.IS_FALLBACK_SYSTEM
            ? [
                {
                    path: routePaths.ARCGIS_AUTHORIZE,
                    exact: true,
                    component: AuthRedirectionScreen,
                    locationChangeSideEffects: [
                        [arcGISAuth.authorize],
                    ],
                },
                {
                    path: routePaths.ARCGIS_AUTHENTICATE,
                    exact: true,
                    component: AuthRedirectionScreen,
                    locationChangeSideEffects: [
                        [arcGISAuth.authenticate],
                    ],
                },
            ] : []),
        {
            path: routePaths.SYSTEM_ACCESS_DENIED,
            exact: true,
            component: AccessDeniedScreen,
            locationChangeSideEffects: [
                [keycloakAuth.loadAuthSession],
            ],
        },
        {
            component: ({route}) => renderRoutes(route.routes),
            locationChangeSideEffects: [
                [applicationLoaderSagas.ensureRoute],
                [keycloakAuth.loadAuthSession],
                ...(!config.IS_FALLBACK_SYSTEM ? [[arcGISAuth.loadAuthSession]] : []),
                [userProfileLoaderSagas.loadUserProfile],
                [ecsAccessControl.authenticateSystemAccess],
                [userProfileLoaderSagas.loadUserProfiles],
                [ecsAccessControl.authenticateRouteAccess],
                [serviceCaseLoaderSagas.loadServiceCaseLockStatus],
            ],
            routes: [
                {
                    path: routePaths.ROOT,
                    exact: true,
                    component: () => null,
                    locationChangeSideEffects: [],
                },
                {
                    path: routePaths.ROUTE_ACCESS_DENIED,
                    exact: true,
                    component: AccessDeniedScreen,
                    locationChangeSideEffects: [],
                },
                {
                    path: routePaths.SHIFT_ROLE_SELECTION,
                    exact: true,
                    component: ShiftRoleSelectionScreen,
                    locationChangeSideEffects: [],
                },
                {
                    path: routePaths.DASHBOARD_SECTION,
                    component: DashboardSectionScreen,
                    locationChangeSideEffects: [],
                    routes: [
                        {
                            path: routePaths.SERVICE_CASE_SEARCH,
                            exact: true,
                            component: ServiceCaseSearchScreen,
                            locationChangeSideEffects: [
                                [contractPartnerLoaderSagas.loadContractPartners],
                                [serviceCaseLoaderSagas.loadServiceCaseSearchResults],
                                [serviceCaseLoaderSagas.loadServiceCaseCommissioners],
                            ],
                        },
                        {
                            path: routePaths.DASHBOARD,
                            exact: true,
                            component: DashboardScreen,
                            locationChangeSideEffects: [
                                [dashboardLoaderSagas.startAutoRefreshTimer],
                                [dashboardLoaderSagas.loadLoggedInUsers],
                                [dashboardLoaderSagas.loadCurrentUsersTasks],
                                [dashboardLoaderSagas.loadCurrentUserWorkload],
                                [dashboardLoaderSagas.loadForeignAssistanceCases],
                                [dashboardLoaderSagas.loadMemberAppSubmittedCases],
                                [dashboardLoaderSagas.loadOpenServiceAssignments],
                                [dashboardLoaderSagas.loadSickPersonAndVehicleTransportAssignments],
                                [serviceCaseLoaderSagas.loadServiceCaseCommissioners],
                            ],
                        },
                        {
                            path: routePaths.MEMBER_SEARCH,
                            exact: true,
                            component: MemberSearchScreen,
                            locationChangeSideEffects: [
                                [memberLoaderSagas.loadMemberSearchResults],
                            ],
                        },
                        ...(!config.IS_FALLBACK_SYSTEM ? [
                            {
                                path: routePaths.TASK_SEARCH,
                                exact: true,
                                component: TaskSearchScreen,
                                locationChangeSideEffects: [
                                    [taskLoaderSagas.loadTaskSearchResults],
                                ],
                            },
                        ] : []),
                        {
                            path: routePaths.CONTRACT_PARTNER_SEARCH,
                            exact: true,
                            component: ContractPartnerSearchScreen,
                            locationChangeSideEffects: [
                                [contractPartnerLoaderSagas.loadContractPartnerSearchResults],
                                [contractPartnerLoaderSagas.loadContractPartnerAccountManagers],
                            ],
                        },
                        {
                            path: '*',
                            component: NotFoundScreen,
                        },
                    ],
                },
                {
                    path: routePaths.MEMBER,
                    component: MemberScreen,
                    locationChangeSideEffects: [
                        [memberLoaderSagas.loadMemberDetails],
                        [memberLoaderSagas.loadMemberVehicles],
                        [memberLoaderSagas.loadMemberServiceCases],
                        [memberLoaderSagas.loadMemberVehicleInfo],
                    ],
                },
                {
                    path: routePaths.CONTRACT_PARTNER,
                    exact: true,
                    component: ContractPartnerScreen,
                    locationChangeSideEffects: [
                        [contractPartnerLoaderSagas.loadContractPartner],
                        [contractPartnerLoaderSagas.loadContractPartnerTemporaryRestrictions],
                        [contractPartnerLoaderSagas.loadContractPartnerServiceCaseSearchResults],
                        [contractPartnerLoaderSagas.loadACECommissioners],
                        [contractPartnerLoaderSagas.loadContractPartnerAccountManagers],
                    ],
                },
                {
                    path: routePaths.SERVICE_CASE_SECTION,
                    component: ({route}) => renderRoutes(route.routes),
                    locationChangeSideEffects: [
                        [serviceCaseLoaderSagas.loadServiceCase],
                        [serviceCaseLoaderSagas.loadServiceCaseTaskCount],
                        [serviceAssignmentLoaderSagas.loadServiceTypeDamageNodes],
                        [serviceCaseLoaderSagas.loadServiceCaseToDoTaskCount],
                    ],
                    routes: [
                        {
                            path: routePaths.SERVICE_ASSIGNMENT_VEHICLE,
                            exact: true,
                            component: props => (
                                <Suspense fallback={null}>
                                    <ServiceAssignmentVehicleScreenLazy {...props} />
                                </Suspense>
                            ),
                            locationChangeSideEffects: [
                                [contractPartnerLoaderSagas.loadContractPartners],
                                [serviceCaseLoaderSagas.loadServiceCaseVehicleInfo],
                                [serviceAssignmentLoaderSagas.loadSAVMapLayers],
                                [serviceAssignmentLoaderSagas.loadSAVMapDamageLocationWatcher],
                                [serviceAssignmentLoaderSagas.loadSAVMapTowingDestinationWatcher],
                                [serviceAssignmentLoaderSagas.loadSAVMapContractPartnerWatcher],
                                [serviceAssignmentLoaderSagas.loadSAVNearbyTowingDestinations],
                                [serviceAssignmentLoaderSagas.loadSAVContractPartnerRecommendations],
                                [serviceAssignmentLoaderSagas.loadServiceAssignmentQMFeedbackDraft],
                                [serviceAssignmentLoaderSagas.loadServiceAssignmentFinalDestinationRoute],
                                [serviceAssignmentLoaderSagas.loadContractPartnerToDamageLocationRoute],
                            ],
                        },
                        {
                            path: routePaths.SERVICE_ASSIGNMENT_RENTAL_CAR,
                            exact: true,
                            component: props => (
                                <Suspense fallback={null}>
                                    <ServiceAssignmentRentalCarScreenLazy {...props} />
                                </Suspense>
                            ),
                            locationChangeSideEffects: [
                                [serviceAssignmentLoaderSagas.loadSARCMapLayers],
                                [serviceAssignmentLoaderSagas.loadSARCMapRentalCarStationWatcher],
                                [serviceAssignmentLoaderSagas.loadSARCNearbyRentalCarStations],
                                [serviceAssignmentLoaderSagas.loadSARCACEPartnerData],
                                [serviceAssignmentLoaderSagas.loadBudgetsByServiceType],
                                [serviceAssignmentLoaderSagas.loadBudgetMaxDurations],
                            ],
                        },
                        {
                            path: routePaths.SERVICE_ASSIGNMENT_ACCOMMODATION,
                            exact: true,
                            component: props => (
                                <Suspense fallback={null}>
                                    <ServiceAssignmentAccommodationScreenLazy {...props} />
                                </Suspense>
                            ),
                            locationChangeSideEffects: [
                                [serviceAssignmentLoaderSagas.loadSAAMapLayers],
                                [serviceAssignmentLoaderSagas.loadSAAMapAccommodationLocationWatcher],
                                [serviceAssignmentLoaderSagas.loadSAAAccommodationLocations],
                                [serviceAssignmentLoaderSagas.loadBudgetsByServiceType],
                                [serviceAssignmentLoaderSagas.loadBudgetMaxDurations],
                            ],
                        },
                        {
                            path: routePaths.SERVICE_ASSIGNMENT_SHORT_TRIP,
                            exact: true,
                            component: props => (
                                <Suspense fallback={null}>
                                    <ServiceAssignmentShortTripScreenLazy {...props} />
                                </Suspense>
                            ),
                            locationChangeSideEffects: [
                                [serviceAssignmentLoaderSagas.loadBudgetsByServiceType],
                            ],
                        },
                        {
                            path: routePaths.SERVICE_ASSIGNMENT_RETURN_JOURNEY,
                            exact: true,
                            component: props => (
                                <Suspense fallback={null}>
                                    <ServiceAssignmentReturnJourneyScreenLazy {...props} />
                                </Suspense>
                            ),
                            locationChangeSideEffects: [
                                [serviceAssignmentLoaderSagas.loadServiceProviderData],
                            ],
                        },
                        {
                            path: routePaths.SERVICE_ASSIGNMENT_SUBSTITUTE_DRIVER,
                            exact: true,
                            component: props => (
                                <Suspense fallback={null}>
                                    <ServiceAssignmentSubstituteDriverScreenLazy {...props} />
                                </Suspense>
                            ),
                            locationChangeSideEffects: [
                                [serviceAssignmentLoaderSagas.loadServiceProviderData],
                                [serviceCaseLoaderSagas.loadServiceCaseVehicleInfo],
                            ],
                        },
                        {
                            path: routePaths.SERVICE_ASSIGNMENT_MEDICAL_CLEARANCE,
                            exact: true,
                            component: props => (
                                <Suspense fallback={null}>
                                    <ServiceAssignmentMedicalClearanceScreenLazy {...props} />
                                </Suspense>
                            ),
                            locationChangeSideEffects: [
                                [serviceAssignmentLoaderSagas.loadServiceProviderData],
                                [serviceCaseLoaderSagas.loadServiceCaseVehicleInfo],
                            ],
                        },
                        {
                            path: routePaths.SERVICE_ASSIGNMENT_SICK_PERSON,
                            exact: true,
                            component: props => (
                                <Suspense fallback={null}>
                                    <ServiceAssignmentSickPersonScreenLazy {...props} />
                                </Suspense>
                            ),
                            locationChangeSideEffects: [
                                [serviceAssignmentLoaderSagas.loadServiceProviderData],
                            ],
                        },
                        {
                            path: routePaths.SERVICE_ASSIGNMENT_VEHICLE_RETURN,
                            exact: true,
                            component: props => (
                                <Suspense fallback={null}>
                                    <ServiceAssignmentVehicleReturnScreenLazy {...props} />
                                </Suspense>
                            ),
                            locationChangeSideEffects: [
                                [serviceAssignmentLoaderSagas.loadServiceProviderData],
                            ],
                        },
                        {
                            path: routePaths.SERVICE_ASSIGNMENT_VEHICLE_ADDITIONAL,
                            exact: true,
                            component: props => (
                                <Suspense fallback={null}>
                                    <ServiceAssignmentVehicleAdditionalScreenLazy {...props} />
                                </Suspense>
                            ),
                            locationChangeSideEffects: [
                                [contractPartnerLoaderSagas.loadContractPartners],
                                [serviceAssignmentLoaderSagas.loadSAVAMapLayers],
                                [serviceAssignmentLoaderSagas.loadSAVAMapServiceLocationWatcher],
                                [serviceAssignmentLoaderSagas.loadSAVAMapTowingDestinationWatcher],
                                [serviceAssignmentLoaderSagas.loadSAVAMapContractPartnerWatcher],
                                [serviceAssignmentLoaderSagas.loadSAVANearbyTowingDestinations],
                                [serviceAssignmentLoaderSagas.loadSAVAContractPartnerRecommendations],
                                [serviceAssignmentLoaderSagas.loadServiceAssignmentQMFeedbackDraft],
                                [serviceAssignmentLoaderSagas.loadServiceAssignmentFinalDestinationRoute],
                                [serviceAssignmentLoaderSagas.loadContractPartnerToDamageLocationRoute],
                            ],
                        },
                        {
                            path: routePaths.SERVICE_ASSIGNMENT_OTHER_SERVICES,
                            exact: true,
                            component: props => (
                                <Suspense fallback={null}>
                                    <ServiceAssignmentOtherServicesScreenLazy {...props} />
                                </Suspense>
                            ),
                            locationChangeSideEffects: [
                                [serviceAssignmentLoaderSagas.loadServiceProviderData],
                            ],
                        },
                        {
                            path: routePaths.SERVICE_ASSIGNMENT_PICKUP,
                            exact: true,
                            component: props => (
                                <Suspense fallback={null}>
                                    <ServiceAssignmentPickupScreenLazy {...props} />
                                </Suspense>
                            ),
                            locationChangeSideEffects: [
                                [contractPartnerLoaderSagas.loadContractPartners],
                                [serviceAssignmentLoaderSagas.loadSAPMapLayers],
                                [serviceAssignmentLoaderSagas.loadSAPMapContractPartnerWatcher],
                                [serviceAssignmentLoaderSagas.loadSAPContractPartnerRecommendations],
                                [serviceAssignmentLoaderSagas.loadSAPMapPickupToTowingStaticRoute],
                                [serviceAssignmentLoaderSagas.loadServiceAssignmentQMFeedbackDraft],
                                [serviceAssignmentLoaderSagas.loadContractPartnerToDamageLocationRoute],
                                [serviceAssignmentLoaderSagas.loadPickupPrices],
                            ],
                        },
                        {
                            path: routePaths.SERVICE_ASSIGNMENT_TRAILER_RETURN,
                            exact: true,
                            component: props => (
                                <Suspense fallback={null}>
                                    <ServiceAssignmentTrailerReturnScreenLazy {...props} />
                                </Suspense>
                            ),
                            locationChangeSideEffects: [
                                [serviceAssignmentLoaderSagas.loadServiceProviderData],
                            ],
                        },
                        {
                            path: routePaths.SERVICE_ASSIGNMENT_DEATH_EVENT_HELP,
                            exact: true,
                            component: props => (
                                <Suspense fallback={null}>
                                    <ServiceAssignmentDeathEventHelpScreenLazy {...props} />
                                </Suspense>
                            ),
                            locationChangeSideEffects: [
                                [serviceAssignmentLoaderSagas.loadServiceProviderData],
                                [serviceCaseLoaderSagas.loadServiceCaseVehicleInfo],
                            ],
                        },
                        {
                            path: routePaths.SERVICE_ASSIGNMENT_MEDICINE_SHIPMENT,
                            exact: true,
                            component: props => (
                                <Suspense fallback={null}>
                                    <ServiceAssignmentMedicineShipmentScreenLazy {...props} />
                                </Suspense>
                            ),
                            locationChangeSideEffects: [
                                [serviceAssignmentLoaderSagas.loadServiceProviderData],
                                [serviceCaseLoaderSagas.loadServiceCaseVehicleInfo],
                            ],
                        },
                        {
                            path: routePaths.SERVICE_ASSIGNMENT_MEDICAL_ADVICE,
                            exact: true,
                            component: props => (
                                <Suspense fallback={null}>
                                    <ServiceAssignmentMedicalAdviceScreenLazy {...props} />
                                </Suspense>
                            ),
                            locationChangeSideEffects: [
                                [serviceAssignmentLoaderSagas.loadSAMAMapLayers],
                                [serviceAssignmentLoaderSagas.loadSAMAMapDoctorLocationWatcher],
                                [serviceAssignmentLoaderSagas.loadSAMANearbyDoctors],
                                [serviceAssignmentLoaderSagas.loadSAMAMapServiceProviderWatcher],
                                [serviceAssignmentLoaderSagas.loadSAMAServiceProviders],
                            ],
                        },
                        {
                            path: routePaths.SERVICE_ASSIGNMENT_LEGAL_ADVICE,
                            exact: true,
                            component: props => (
                                <Suspense fallback={null}>
                                    <ServiceAssignmentLegalAdviceScreenLazy {...props} />
                                </Suspense>
                            ),
                            locationChangeSideEffects: [
                                [serviceAssignmentLoaderSagas.loadServiceProviderData],
                            ],
                        },
                        // should be last in this group of routes!
                        {
                            path: routePaths.SERVICE_CASE,
                            exact: true,
                            component: ServiceCaseScreen,
                            locationChangeSideEffects: [
                                [serviceCaseLoaderSagas.loadServiceCaseVehicleInfo],
                                [serviceCaseLoaderSagas.loadServiceCaseTasks],
                                [caseLogLoaderSagas.loadCaseLog],
                                [caseLogLoaderSagas.loadFilterCaseLogs],
                                [serviceCaseLoaderSagas.loadShortTripBudget],
                            ],
                        },
                    ],
                },
                {
                    path: '*',
                    component: NotFoundScreen,
                },
            ],
        },
    ],
}];

export default getRoutes;
